import React, {useEffect, useState} from 'react';
import _ from "lodash";
import "./MediaEdit.scss";
import {PageLoader} from "../../components/Loader";
import InformationGeneral from "./components/informationGeneral/InformationGeneral";
import {Button} from "../../components/Button";
import {Can} from "../../../ability-context";
import SelectCoverVideo from "./components/selectCoverVideo/SelectCoverVideo";
import Categories from "./components/categories/Categories";
import Alert from "../../components/Alert/Alert";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {deleteMedia, fetchMedia, MEDIA_KEY, MEDIAS_KEY, updateMedia} from "../../../api/public-voissa/medias";
import {toastError} from "../../../libs/toast";
import {MEDIA} from "../../../const/toasts";
import {ALBUM_MEDIAS_KEY} from "../../../api/public-voissa/albums";
import SelectBar from '../SelectBar/SelectBar';
import Drawer from "../Drawer/Drawer";
import convertToNext from "../../../libs/lexical/convertissors/simple";
import {fetchUserFriends, USER_CURRENT_KEY, USER_FRIENDS_KEY} from "../../../api/public-voissa/users";

const MediaEdit = ({
        currentMediaId = null,
        handleClose = () =>{}
    }) =>{
    const queryClient = useQueryClient();
    const dataCurrentUser = queryClient.getQueryData([USER_CURRENT_KEY]);
    const currentUserId = _.get(dataCurrentUser, '_id');
    const isCurrentUser = !!currentUserId;
    const [currentMedia, setCurrentMedia] = useState({});
    const [currentTab, setCurrentTab] = useState(0);
    const [toggleAlertDeleteMedia, setToggleAlertDeleteMedia] = useState(false);

    const {isSuccess : isSuccessMedia, isLoading : isLoadingMedia, data : dataMedia} = useQuery(
        [MEDIA_KEY, currentMediaId],
        ()=> fetchMedia(currentMediaId),
        {
            staleTime : 0,
            cacheTime : 0,
            enabled : !!currentMediaId,
            placeholderData : {}
        }
    );

    const {
        mutate : mutateUpdateMedia,
    } = useMutation((data) => updateMedia(currentMediaId, data),{
        onSuccess: async (data) =>{
            await queryClient.invalidateQueries([MEDIAS_KEY]);
            await queryClient.invalidateQueries([MEDIA_KEY, currentMediaId]);
            await queryClient.invalidateQueries([ALBUM_MEDIAS_KEY, _.get(data,'album._id')]);
        },
        onError: () =>{
            toastError(MEDIA.ERROR.UPDATE);
        }
    });

    const {
        mutate : mutateDeleteMedia,
    } = useMutation(() => deleteMedia(currentMediaId),{
        onSuccess: async (data) =>{
            await queryClient.invalidateQueries([MEDIAS_KEY]);
            await queryClient.invalidateQueries([MEDIA_KEY, currentMediaId]);
            await queryClient.invalidateQueries([ALBUM_MEDIAS_KEY, _.get(data,'album')]);
        },
        onError: () =>{
            toastError(MEDIA.ERROR.UPDATE);
        }
    });



    const {data : dataFriends} = useQuery(
        [USER_FRIENDS_KEY, currentUserId, {limit: false}],
        ()=> fetchUserFriends(currentUserId, {limit: false}),
        {
            staleTime : (1000 * 60 * 5),
            enabled : !!isCurrentUser,
            placeholderData : {
                data : [],
                total : 0
            },
            select : (data) =>{
                return {
                    friends : data.data || [],
                    total : data.total
                }
            }
        }
    );

    let {friends = []} = dataFriends || {};

    function handleDeleteMedia() {
        mutateDeleteMedia();
        setToggleAlertDeleteMedia(false);
        handleClose();
    }

    function handleSubmit() {
        if(currentMedia.legend.length >0 ){
            mutateUpdateMedia(currentMedia);
            handleClose();
        }
    }

    useEffect(() => {
        const handleData = () => {
            if(dataMedia._id){
                setCurrentMedia(dataMedia);
            }
        }
        handleData();

    }, [dataMedia, currentMedia.updatedAt]);

    let cover = "";

    if(isSuccessMedia){
        if(dataMedia.kind === "video"){
            cover = dataMedia.videoCoverUrl ? dataMedia.videoCoverUrl : (dataMedia.previewUrl && dataMedia.previewUrl[0]) || "";
        }
        else{
            cover = dataMedia.previewUrl;
        }
    }

    function handleUpdateContent(editState){
        let {text, links, mentions} = convertToNext(editState);
        setCurrentMedia({...currentMedia, legend : text, links, mentions});
    }


    return <div id={"media-edit"}>
        {!isSuccessMedia || !currentMedia._id ? (
            <PageLoader/>
        ) : (
            <>
                <Drawer
                    open={toggleAlertDeleteMedia}
                    handleClose={()=>setToggleAlertDeleteMedia(false)}
                    headContent={"Attention"}
                >
                    <Alert handleDelete={() => handleDeleteMedia()}
                           handleClose={() => setToggleAlertDeleteMedia(false)}
                           text={`Voulez-vous vraiment supprimer cette publication ?`}/>
                </Drawer>
                <div className={"media-edit-content"}>
                    <div className={"media-edit-thumb"} style={{backgroundImage: `url(${cover}?width=400&height=400)`}} />
                    <SelectBar style={{marginBottom:'8px'}} options={[
                            {label: "Informations", value: 0, active: currentTab === 0},
                            {label: "Tags",  value: 1, active: currentTab === 1},
                            {label: "Miniature",  value: 2, active: currentTab === 2, display: dataMedia.kind === "video"}
                        ]} onSelect={({value})=>setCurrentTab(value)}/>
                    {[
                        <InformationGeneral
                            key={`${dataMedia._id}${dataMedia.updatedAt}`}
                            updatedAt={dataMedia.updatedAt}
                            mediaId={currentMedia._id}
                            legend={currentMedia.legend}
                            mentions={friends}
                            links={currentMedia.links || []}
                            kind={dataMedia.kind}
                            handleUpdateContent={handleUpdateContent}
                        />,
                        <Categories
                            categoriesHistory={dataMedia?.categoriesHistory || {}}
                            categoriesMedia={dataMedia?.categories || []}
                            mediaId={dataMedia?._id || null}
                            handleClose={()=>handleClose()}
                        />,
                        <SelectCoverVideo currentMedia={dataMedia} />
                    ][currentTab]}
                </div>

                {currentTab === 0 ? (
                    <div className={"media-edit-footer"}>
                        <div className={"media-edit-footer-left"}>
                            <Can action={"delete"} subject={dataMedia} modelName="Media">
                                <span className={"media-edit-delete"} onClick={() => setToggleAlertDeleteMedia(true)}>Supprimer cette publication</span>
                            </Can>
                        </div>
                        <div className={"media-edit-footer-right"}>
                            <Button size={"small"}
                                    btnStyle={"grey"}
                                    onClick={() => handleClose()}
                                    content={"Annuler"}/>
                            <Can action={"update"} subject={dataMedia} modelName="Media">
                                <Button btnStyle={"primary-reverse"}
                                        size={"small"}
                                        className={"ml-8"}
                                        disabled={!(currentMedia.legend.length >0)}
                                        loading={isLoadingMedia}
                                        onClick={() => handleSubmit()}
                                        content={"Modifier"}/>
                            </Can>
                        </div>
                    </div>
                ) : null}
            </>
        )}
    </div>
};

export default MediaEdit;